import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, sendOtp, clearError, clearMessage } from '../store/authSlice';
import { useNavigate } from 'react-router-dom';
import { OTP_CONFIG, DEFAULTS, MESSAGES } from '../config';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { formatPhoneForAPI } from '../utils/phoneUtils';
import 'react-phone-number-input/style.css';
import './PhoneInput.css';

const Login = () => {
  const [phoneNumber, setPhoneNumber] = useState(''); // Empty by default
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpAttempts, setOtpAttempts] = useState(0);
  const [rateLimitWarning, setRateLimitWarning] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, message, isAuthenticated, otpLoading, needsRegistration } = useSelector((state) => state.auth);

  // Redirect to registration if user needs to register
  useEffect(() => {
    if (needsRegistration) {
      // Clear any existing registration state to force fresh OTP verification
      localStorage.removeItem('registrationPhone');
      localStorage.removeItem('registrationOtpSent');
      localStorage.removeItem('registrationOtpVerified');
      localStorage.removeItem('registrationOtpTimer');
      localStorage.removeItem('registrationOtpTimestamp');
      localStorage.removeItem('registrationFormData');
      
      // Store the phone number for registration page
      if (phoneNumber) {
        localStorage.setItem('loginPhoneForRegistration', phoneNumber);
      }
      
      const timer = setTimeout(() => {
        navigate('/register');
      }, 3000); // Give user time to read the message
      return () => clearTimeout(timer);
    }
  }, [needsRegistration, navigate, phoneNumber]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (error || message) {
      const timer = setTimeout(() => {
        dispatch(clearError());
        dispatch(clearMessage());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, message, dispatch]);

  // OTP Timer countdown
  useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => {
        setOtpTimer(otpTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [otpTimer]);

  // Validate phone number
  const validatePhone = (phone) => {
    if (!phone) {
      setPhoneError('Phone number is required');
      return false;
    }
    
    if (!isValidPhoneNumber(phone)) {
      setPhoneError('Please enter a valid phone number');
      return false;
    }
    
    setPhoneError('');
    return true;
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    
    // Prevent multiple clicks
    if (isButtonDisabled || otpLoading) return;
    
    // Validate phone number
    if (!validatePhone(phoneNumber)) return;
    
    const formattedPhone = formatPhoneForAPI(phoneNumber);
    console.log('Sending OTP to:', formattedPhone);
    
    setIsButtonDisabled(true);
    setRateLimitWarning('');
    
    try {
      await dispatch(sendOtp({ mobile: formattedPhone })).unwrap();
      setOtpSent(true);
      setOtpTimer(OTP_CONFIG.TIMER_SECONDS);
      setOtp(''); // Clear any existing OTP
      setOtpAttempts(prev => prev + 1);
      
      // Show rate limit warning after first attempt
      if (otpAttempts >= 0) {
        const remaining = OTP_CONFIG.RATE_LIMIT_MAX - (otpAttempts + 1);
        if (remaining > 0) {
          setRateLimitWarning(`${remaining} OTP requests remaining this hour`);
        } else {
          setRateLimitWarning(`Rate limit reached. Wait ${OTP_CONFIG.RATE_LIMIT_WINDOW_HOURS} hour before requesting more OTPs`);
        }
      }
      
    } catch (error) {
      console.error('Failed to send OTP:', error);
      
      // Handle rate limit errors
      if (error.includes('rate limit') || error.includes('Too many')) {
        setRateLimitWarning('Rate limit exceeded. Please wait before requesting another OTP');
      }
    } finally {
      // Re-enable button after configured seconds to prevent spam
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, OTP_CONFIG.BUTTON_DISABLE_SECONDS * 1000);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    
    // Prevent multiple clicks
    if (isLoading) return;
    
    // Validate phone number
    if (!validatePhone(phoneNumber)) return;
    
    const formattedPhone = formatPhoneForAPI(phoneNumber);
    
    if (formattedPhone && otp) {
      dispatch(loginUser({ mobile: formattedPhone, otp }));
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-bulky-navy-900 flex items-center justify-center p-6 font-sans relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-bulky-blue-500/10 rounded-full blur-[120px] -mr-40 -mt-40"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-bulky-navy-500/20 rounded-full blur-[100px] -ml-20 -mb-20"></div>

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden relative z-10 border border-white/10">
        <div className="p-8 pb-4 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white p-3 rounded-2xl shadow-xl shadow-bulky-blue-500/10 border border-gray-50 transform -rotate-3 hover:rotate-0 transition-transform duration-500">
              <img
                src="/bm-logo.png"
                alt="Bulky Marketing Logo"
                className="h-12 w-12 object-contain"
              />
            </div>
          </div>
          {/* <h2 className="font-medium">Bulky</h2> */}
          <p className="text-bulky-blue-500 font-normal uppercase tracking-[0.3em] text-[10px] mb-4">Marketing System</p>
          <div className="flex justify-center gap-1.5 mb-2">
            <div className="h-1 w-8 bg-bulky-blue-500 rounded-full"></div>
            <div className="h-1 w-2 bg-bulky-blue-200 rounded-full"></div>
          </div>
        </div>

        <form onSubmit={handleLogin} className="px-6 pb-6">
          <div className="mb-3">
            <label htmlFor="phone" className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-2 ml-1">
              Mobile Number
            </label>
            <div className="relative">
              <PhoneInput
                international
                defaultCountry={DEFAULTS.COUNTRY}
                value={phoneNumber}
                onChange={setPhoneNumber}
                disabled={otpSent}
                className={`phone-input w-full ${otpSent ? 'disabled' : ''}`}
                placeholder="+91 98765 43210"
              />
              {phoneError && (
                <p className="text-red-500 text-xs mt-1 ml-1">{phoneError}</p>
              )}
            </div>
          </div>

          {!otpSent ? (
            <>
              <button
                type="button"
                onClick={handleSendOtp}
                className={`w-full bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white font-normal py-3 px-4 rounded-xl transition-all duration-300 shadow-xl shadow-bulky-blue-500/20 active:scale-95 flex items-center justify-center gap-2 text-sm mb-3 ${
                  otpLoading || !phoneNumber || isButtonDisabled || !isValidPhoneNumber(phoneNumber || '') ? 'opacity-70 cursor-not-allowed' : ''
                }`}
                disabled={otpLoading || !phoneNumber || isButtonDisabled || !isValidPhoneNumber(phoneNumber || '')}
              >
                {otpLoading ? (
                  <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                ) : (
                  <>
                    <span className="material-icons-round">send</span>
                    <span>{isButtonDisabled ? 'Please wait...' : 'Send OTP'}</span>
                  </>
                )}
              </button>
              
              {/* Compact Info Box */}
              <div className="mb-3 p-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span className="material-icons-round text-blue-500 text-sm">info</span>
                  <p className="text-xs font-normal">OTP via WhatsApp</p>
                </div>
                <p className="text-xs ml-6">{MESSAGES.RATE_LIMIT}</p>
                <p className="text-xs ml-6 mt-1">Check your mobile device (not WhatsApp Web)</p>
                {rateLimitWarning && (
                  <p className="text-xs ml-6 mt-1 font-normal text-amber-600">{rateLimitWarning}</p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="mb-3">
                <label htmlFor="otp" className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-2 ml-1">
                  OTP {otpTimer > 0 && <span className="text-bulky-blue-500">({formatTime(otpTimer)})</span>}
                </label>
                <div className="relative group">
                  <span className="material-icons-round absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-bulky-blue-500 transition-colors text-sm">lock_open</span>
                  <input
                    type="text"
                    id="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter 6-digit OTP"
                    maxLength={OTP_CONFIG.LENGTH}
                    required
                    className="w-full pl-10 pr-3 py-3 bg-gray-50 border-2 border-transparent rounded-xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all duration-300 font-normal text-bulky-navy-800 tracking-[0.2em] text-sm"
                  />
                </div>
              </div>

              <button
                type="submit"
                onClick={handleLogin}
                className={`w-full bg-bulky-navy-500 hover:bg-bulky-blue-600 text-white font-normal py-3 px-4 rounded-xl transition-all duration-300 shadow-xl shadow-bulky-blue-500/20 active:scale-95 flex items-center justify-center gap-2 text-sm mb-3 ${
                  isLoading || !phoneNumber || !otp || otp.length !== OTP_CONFIG.LENGTH || !isValidPhoneNumber(phoneNumber || '') ? 'opacity-70 cursor-not-allowed' : ''
                }`}
                disabled={isLoading || !phoneNumber || !otp || otp.length !== OTP_CONFIG.LENGTH || !isValidPhoneNumber(phoneNumber || '')}
              >
                {isLoading ? (
                  <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                ) : (
                  <>
                    <span>Access Dashboard</span>
                    <span className="material-icons-round">arrow_forward</span>
                  </>
                )}
              </button>

              {/* Compact OTP Info */}
              <div className="mb-3 p-2 bg-green-50 border border-green-200 text-green-700 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="material-icons-round text-green-500 text-sm">sms</span>
                  <p className="text-xs">Check your mobile WhatsApp • Expires in {formatTime(otpTimer)}</p>
                </div>
              </div>

              {otpTimer === 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setOtpSent(false);
                    setOtp('');
                    setRateLimitWarning('');
                  }}
                  className="w-full bg-gray-500 hover:bg-gray-600 text-white font-normal py-2 px-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 text-sm"
                >
                  <span className="material-icons-round">refresh</span>
                  <span>Send New OTP</span>
                </button>
              )}
            </>
          )}
        </form>

        {error && (
          <div className={`mx-8 mb-6 p-3 border-l-4 rounded-lg flex items-center gap-2 ${
            needsRegistration 
              ? 'bg-blue-50 border-blue-500 text-blue-700' 
              : 'bg-red-50 border-red-500 text-red-700'
          }`}>
            <span className={`material-icons-round text-sm ${
              needsRegistration ? 'text-blue-500' : 'text-red-500'
            }`}>
              {needsRegistration ? 'info' : 'error_outline'}
            </span>
            <div className="flex-1">
              <p className="text-xs font-normal">{error}</p>
              {needsRegistration && (
                <div className="mt-2">
                  <p className="text-xs mb-2">Redirecting to registration in 3 seconds...</p>
                  <button
                    onClick={() => {
                      // Store phone number for registration
                      if (phoneNumber) {
                        localStorage.setItem('loginPhoneForRegistration', phoneNumber);
                      }
                      navigate('/register');
                    }}
                    className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg font-normal"
                  >
                    Register Now
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {message && (
          <div className="mx-8 mb-6 p-3 bg-blue-50 border-l-4 border-bulky-blue-500 text-bulky-blue-700 rounded-lg flex items-center gap-2">
            <span className="material-icons-round text-bulky-blue-500 text-sm">check_circle_outline</span>
            <p className="text-xs font-normal">{message}</p>
          </div>
        )}

        <div className="bg-gray-50 p-4 text-center border-t border-gray-100">
          <p className="text-[9px] text-gray-400 font-normal uppercase tracking-wider mb-1">Authenticated Entry Only</p>
          <p className="text-xs text-bulky-navy-300 font-normal mb-2">Contact system administrator for lost access keys.</p>
          <button
            onClick={() => {
              // Store phone number for registration
              if (phoneNumber) {
                localStorage.setItem('loginPhoneForRegistration', phoneNumber);
              }
              navigate('/register');
            }}
            className="text-bulky-blue-500 hover:text-bulky-blue-600 text-sm font-normal"
          >
            New user? Create account
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
